import { message as $message } from 'antd'
import { useMemoizedFn } from 'ahooks'
import Big from 'big.js'
import {
    downloadUpdatePriceTpl,
    executeModifyPriceJob,
    queryOnlineSkuList,
    saveJob,
    saveJobAndExecute
} from '@/services/shopGoodsImport'
import { useState } from 'react'
import uniqBy from 'lodash/uniqBy'
import { checkCompanyAuthApi } from '@/services/order'
import { queryPriceFluctuationV2 } from '@/services/sotrPriceSyncTool'

export interface InputPriceData {
    itemCode: string
    newPrice: string
    newMultiPrice?: string
}

export enum FLOAT_TYPE {
    FLOAT_20 = 'TWENTY_PERCENT',
    FLOAT_40 = 'FORTY_PERCENT',
    FLOAT_60 = 'SIXTY_PERCENT',
    FLOAT_CUSTOM = 'CUSTOM_PERCENT'
}

export const FloatTypeValue = {
    [FLOAT_TYPE.FLOAT_20]: 0.2,
    [FLOAT_TYPE.FLOAT_40]: 0.4,
    [FLOAT_TYPE.FLOAT_60]: 0.6
}

const transPriceFloatValue = value => {
    return Big(value).times(100).toNumber()
}

export default () => {
    const [taskId, setTaskId] = useState<string>('')
    const [editJobTaskId, setEditJobTaskId] = useState<string | null>()
    const [selectedGoods, setSelectedGoods] = useState<string[]>([])
    const [onlineSkuList, setOnlineSkuList] = useState<any[]>([])
    const [listSelected, setListSelected] = useState([])
    const [selectedRows, setSelectedRows] = useState([])
    const [inputPriceData, setInputPriceData] = useState<InputPriceData[]>([])
    const [priceFloat, setPriceFloat] = useState<FLOAT_TYPE>(FLOAT_TYPE.FLOAT_60)
    const [priceFloatValue, setPriceFloatValue] = useState(FloatTypeValue[FLOAT_TYPE.FLOAT_60])
    const ItemCodesData = uniqBy(
        onlineSkuList?.map(({ itemCode }) => ({ itemCode, itemName: itemCode })),
        'itemCode'
    )

    const [goodsModalVisible, setGoodsModalVisible] = useState<{
        visible: boolean
        isEdit?: boolean
    }>({ visible: false })
    const [syncDrawerVisible, setSyncDrawerVisible] = useState<{
        taskId?: string
        visible: boolean
        isEdit?: boolean
    }>({ visible: false })

    const queryCurSyncPriceFloat = useMemoizedFn(async () => {
        try {
            const { success, data } = await queryPriceFluctuationV2()
            if (success) {
                const { number: priceFloatValue, type: priceFloat } = data
                setPriceFloat(priceFloat)
                setPriceFloatValue(transPriceFloatValue(priceFloatValue))

                return {
                    priceFloat,
                    priceFloatValue
                }
            }
        } catch (e) {
            console.error('catchError', e)
        }
    })

    const onDownloadUpdatePriceTpl = async () => {
        try {
            const { success, message } = await downloadUpdatePriceTpl()

            if (success) {
                $message.success(message)
            } else {
                $message.error(message)
            }
        } catch (error) {
            console.error('catchError', error)
        }
    }

    const checkLogisticsAuth = async () => {
        try {
            const { success, message } = await checkCompanyAuthApi({
                operationEnum: 'MODIFY_SHOP_GOODS_PRICE'
            })
            if (success) {
                return true
            } else {
                $message.error(message)
                return false
            }
        } catch (error) {
            console.error('catchError', error)
            return false
        }
    }
    const onQueryOnlineSkuList = async (data: any) => {
        try {
            const { success, message, data: dataSource } = await queryOnlineSkuList(data)
            if (success) {
                setOnlineSkuList(dataSource)
            } else {
                $message.error(message)
            }
        } catch (error) {
            console.error('catchError', error)
        }
    }

    const selectedData = selectedRows?.map(
        ({
            colourSpec,
            itemCode,
            itemName,
            newMultiPrice,
            newPrice,
            oldMultiPrice,
            oldPrice,
            onlineSkuId,
            onlineSpuId,
            pic,
            shopId,
            shopName,
            shopType,
            shopLink
        }) => ({
            colourSpec,
            itemCode,
            itemName,
            newMultiPrice,
            newPrice,
            oldMultiPrice,
            oldPrice,
            onlineSkuId,
            onlineSpuId,
            pic,
            shopId,
            shopName,
            shopType,
            taskId,
            shopLink
        })
    )

    const onSave = async () => {
        try {
            const mergeData = selectedData?.map(item => {
                const findSelectedData = inputPriceData?.find(
                    ({ inputPriceData }) => inputPriceData === item?.inputPriceData
                )
                return {
                    ...item,
                    ...findSelectedData
                }
            })
            const { success, message } = await saveJob(mergeData)

            if (success) {
                setInputPriceData([])
                $message.success('保存成功')
                setSyncDrawerVisible({ visible: false })
            } else {
                $message.error(message)
            }
        } catch (error) {
            console.error('catchError', error)
        }
    }

    const onSaveAndRun = async () => {
        const result = checkLogisticsAuth()
        if (!result) {
            try {
                const { success, message } = await saveJobAndExecute(selectedData)
                if (success) {
                    $message.success('保存并执行成功')
                    setInputPriceData([])
                    setSyncDrawerVisible({ visible: false })
                } else {
                    $message.error(message)
                }
            } catch (error) {
                console.error('catchError', error)
            }
        }
    }

    const onRun = async (taskId: string) => {
        try {
            const { success, message } = await executeModifyPriceJob({ taskId })
            if (success) {
                $message.success('执行成功')
            } else {
                $message.error(message)
            }
        } catch (error) {
            console.error('catchError', error)
        }
    }

    return {
        onDownloadUpdatePriceTpl,
        onQueryOnlineSkuList,
        ItemCodesData,
        setTaskId,
        taskId,
        setEditJobTaskId,
        editJobTaskId,
        selectedGoods,
        setSelectedGoods,
        goodsModalVisible,
        setGoodsModalVisible,
        syncDrawerVisible,
        setSyncDrawerVisible,
        onSave,
        onRun,
        onSaveAndRun,
        listSelected,
        setListSelected,
        selectedRows,
        setSelectedRows,
        selectedData,
        inputPriceData,
        setInputPriceData,
        priceFloat,
        priceFloatValue,
        setPriceFloat,
        queryCurSyncPriceFloat
    }
}
